/**
 * PtyHarness — drives the `claude` CLI under a pseudo-terminal behind the
 * generic `AgentHarness` interface.  Selected at runtime via
 * `createHarness("pty")`. It is the UNCONDITIONAL harness for the task chat
 * (cards); see `resolveHarnessKind()` in `runner/query-bridge.ts`. The only way
 * to put a card on the SDK harness is the maintainer override
 * `CONVEYOR_FORCE_SDK_CARDS=1`.
 */

import type {
  AgentHarness,
  HarnessEvent,
  HarnessQueryOptions,
  HarnessToolDefinition,
  HarnessMcpServer,
  HarnessUserMessage,
  PtyBridge,
} from "../types.js";
import { PtySession } from "./session.js";
import { PtyMcpServer } from "./mcp-server.js";
import { ensureClaudeCredentials, ensureClaudeOnboarding } from "./credentials.js";

export { PtySession, PtyMcpServer };

export class PtyHarness implements AgentHarness {
  /**
   * `bridge` relays raw terminal I/O to/from the S2 server (and on to the S5
   * terminal). It is undefined for SDK-only callers and PTY runs that never
   * attach a relay; the session simply discards stdout in that case.
   */
  constructor(private readonly bridge?: PtyBridge) {}

  async *executeQuery(opts: {
    prompt: string | AsyncGenerator<HarnessUserMessage, void, unknown>;
    options: HarnessQueryOptions;
    resume?: string;
  }): AsyncGenerator<HarnessEvent, void> {
    const session = new PtySession(
      opts.prompt,
      opts.options,
      opts.resume ?? opts.options.resume,
      this.bridge,
    );
    // The interactive TUI ignores CLAUDE_CODE_OAUTH_TOKEN (headless-only); in
    // Conveyor cloud envs, materialize it as `<configHome>/.credentials.json`
    // so the CLI starts authenticated instead of at the login-method picker,
    // and complete the onboarding flags + workspace trust in .claude.json so
    // neither an abandoned first-run wizard on the persistent user-home nor
    // the folder-trust dialog can park the TUI.
    // Every PTY spawn (fresh, follow-up, resume, retry, mode restart) flows
    // through here — this is the single PtySession construction site.
    await ensureClaudeCredentials();
    await ensureClaudeOnboarding(process.env, opts.options.cwd);
    await session.start();
    try {
      for await (const event of session.events()) {
        yield event;
      }
    } finally {
      await session.teardown();
    }
  }

  createMcpServer(config: { name: string; tools: HarnessToolDefinition[] }): HarnessMcpServer {
    return new PtyMcpServer(config.name, config.tools);
  }
}
